import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faLink, faThumbtack } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tag from "antd/es/tag";
import cn from "classnames";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";

import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { stopEventPropagation, stopEvents } from "@mapmycustomers/shared/util/browser";
import { Button, fileTypeToIcons, getFileType } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import Path from "@app/enum/Path";
import { getCurrentUserId, getPinnedResourceIds } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { userDisplayName } from "@app/util/formatters";

import { arePinnedResourcesUpdating } from "../../store";
import { pinUnpinResource } from "../../store/actions";
import ActionsButton from "../ActionsButton";
import ResourceInfo from "../ResourceInfo";

import styles from "./ResourceItem.module.scss";

type ResourceItemProps = {
  isTrash: boolean;
  onSelect: (id: Resource["id"], selected: boolean) => void;
  resource: Resource;
  selected: boolean;
} & typeof mapDispatchToProps &
  ReturnType<typeof mapStateToProps>;

const ResourceItem: React.FC<ResourceItemProps> = ({
  currentUserId,
  isTrash,
  onPinUnpinResource,
  onSelect,
  pinnedResourceIds,
  resource,
  selected,
  updatingPinned,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const history = useHistory();
  const { folderId } = useParams<{ folderId?: string }>();

  const sharedResource = resource.userId !== currentUserId;

  const isPinned = pinnedResourceIds.includes(resource.id);

  const handleFileClick = useCallback(() => {
    analytics.clicked(["Resource Tile"]);
    if (resource.file?.id) {
      history.push(
        `${Path.RESOURCES}${folderId ? `/folders/${folderId}` : ""}/files/${resource.id}`
      );
    } else if (resource.url) {
      window.open(resource.url, "_blank");
    }
  }, [analytics, resource.file?.id, resource.url, resource.id, history, folderId]);

  const handleSelect = useCallback(
    (checked: boolean) => {
      onSelect(resource.id, checked);
    },
    [onSelect, resource.id]
  );

  const handlePinUnpin = useCallback(
    (e) => {
      analytics.clicked([`${isPinned ? "Unpin" : "Pin"} Resource`]);
      stopEvents(e);
      onPinUnpinResource({ ids: [resource.id], unpin: isPinned });
    },
    [analytics, isPinned, onPinUnpinResource, resource.id]
  );

  return (
    <div
      className={cn(styles.container, { [styles.selected]: selected })}
      onClick={handleFileClick}
    >
      <div className={styles.left}>
        <div onClick={stopEventPropagation}>
          <Checkbox checked={selected} disabled={sharedResource} onChange={handleSelect} />
        </div>
        <div className={styles.resourceInfo}>
          <FontAwesomeIcon
            className={cn(styles.fileIcon, { [styles.linkIcon]: !resource.file })}
            icon={resource.file ? fileTypeToIcons[getFileType(resource.file)].solid : faLink}
          />
          <ResourceInfo resource={resource} />
        </div>
      </div>
      <div className={styles.right}>
        <Tag>
          {intl.formatMessage(
            {
              id: "resources.list.resource.owner",
              defaultMessage: "<b>Owner:</b> {name}",
              description: "Resource owner name",
            },
            { b: (text) => <b>{text}</b>, name: userDisplayName(resource.user) }
          )}
        </Tag>
        {!isTrash && (
          <Button
            disabled={updatingPinned}
            icon={
              <FontAwesomeIcon
                className={cn(styles.pinIcon, { [styles.pinned]: isPinned })}
                icon={faThumbtack}
              />
            }
            iconOnly
            onClick={handlePinUnpin}
            type="link"
          />
        )}
        <ActionsButton
          folderId={folderId}
          isPinned={isPinned}
          isTrash={isTrash}
          resource={resource}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  pinnedResourceIds: getPinnedResourceIds(state),
  updatingPinned: arePinnedResourcesUpdating(state),
});

const mapDispatchToProps = {
  onPinUnpinResource: pinUnpinResource.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ResourceItem);
